import { Typography, Box, useMediaQuery } from "@mui/material";
import React from "react";
import { useTranslation } from "react-i18next";
import { MilestoneProgressbar } from "../../components/milestone-progressbar/MilestoneProgressbar";
import styles from "./RewardSummaryCard.module.scss";
import DataLoader from "../../components/custom-data-loader/DataLoader";
import RewardSummaryCardSkeleton from "./RewardSummaryCardSkeleton";
import {
  DEFAULT_TIER,
  TIER_IMG_MAP,
} from "../../utility/constants/common-constants";

interface RewardSummaryCardProps {
  data: {
    currentPoints: number;
    optinBonus?: number;
    currentTierDisplayName?: string;
    nextTierMessage?: string;
    milestones: number[];
    maxValue: number;
    currentTierClassificationId?: number;
    longDescription?: string;
  };
  isLoading: boolean;
  error: unknown;
}

const RewardSummaryCard: React.FC<RewardSummaryCardProps> = ({
  data,
  isLoading,
  error,
}) => {
  const { t } = useTranslation();
  const isMobile = useMediaQuery("(max-width: 900px)");

  const currentPoints = data?.currentPoints ?? 0;

  return (
    <DataLoader
      isLoading={isLoading}
      error={error}
      data={data}
      loadingComponent={<RewardSummaryCardSkeleton />}
      emptyComponent={<Typography>{t("noData")}</Typography>}
      errorComponent={
        <Typography color="error">
          {t("myRewardsPage.errorLoadingData")}
        </Typography>
      }
    >
      {() => (
        <Box className={styles.container}>
          <Box className={styles.cardHeader}>
            {/* Wavy Background */}
            <Box className={styles.wavyBackground} />
            <Box className={styles.gemWrapper}>
              <Box
                component="img"
                src={
                  TIER_IMG_MAP[
                    data?.currentTierClassificationId ?? DEFAULT_TIER
                  ]
                }
                alt={data?.currentTierDisplayName ?? "Tier"}
                className={styles.gemImage}
              />
            </Box>
          </Box>
          <Box p={2}>
            <Typography
              variant="bodySmall"
              color="secondary.main"
              textAlign="center"
              component="div"
              fontWeight={700}
              letterSpacing={"0.72px"}
            >
              {t("rewardsWidget.youAreA")}
            </Typography>
            <Typography variant="h2" mb={2} textAlign="center">
              {data?.currentTierDisplayName}
            </Typography>
            {data?.longDescription && (
              <Typography variant="body1" mb={2} textAlign="center">
                {data.longDescription}
              </Typography>
            )}
            <Box mb={2} mt={5} px={{ xs: 1, md: 3 }}>
              <Box display="flex" justifyContent="space-between" mb={1} alignItems="flex-start">
                <Box display="flex" alignItems="center">
                <Typography variant="subtitle1" mr={1}>
                  {currentPoints}
                </Typography>
                <Typography variant="body2" textTransform={"lowercase"}>
                  {t("points")}
                </Typography>
                </Box>
                
                <Box maxWidth= {isMobile? "60%" : "70%"} textAlign="left">
                <Typography variant="body2" color="secondary.main">
                  {data?.nextTierMessage}
                </Typography>
                </Box>
              </Box>

              <MilestoneProgressbar
                milestones={data?.milestones}
                currentValue={currentPoints}
                maxValue={data?.maxValue}
              />
            </Box>
          </Box>
        </Box>
      )}
    </DataLoader>
  );
};

export default RewardSummaryCard;
